<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col-md-8">
        <card>
          <!-- Tabs -->
          <div class="row mb-3">
            <div class="col">
              <div
                class="btn-group btn-group-toggle"
                :class="'float-left'"
                data-toggle="buttons"
              >
                <label
                  v-for="(tab, index) in tabs"
                  :key="index + tabKey"
                  class="btn btn-sm btn-primary btn-simple"
                  :class="{ active: tab.active }"
                  :id="index"
                >
                  <input
                    type="radio"
                    @click="switchTab(index)"
                    name="options"
                    autocomplete="off"
                    :checked="tab.active"
                  />
                  <span style="font-size: medium; font-weight: lighter" class="d-none d-sm-block">{{ tab.name }}</span>
                  <span class="d-block d-sm-none">
                    <i :class="tab.icon"></i>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- Profile Tab -->
          <div v-if="activeTab === 'profile'">
            <form @submit.prevent="updateProfile">
              <div class="row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="First name"
                        placeholder="First Name"
                        v-model="user.first_name"
                      >
                      </base-input>
                    </div>
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="Last name"
                        placeholder="Last Name"
                        v-model="user.last_name"
                      >
                      </base-input>
                    </div>
                  </div>
                  <base-input
                    type="email"
                    label="Email address"
                    placeholder="Email"
                    :disabled="true"
                    v-model="user.email"
                  >
                  </base-input>
                  <base-input
                    type="text"
                    label="Phone"
                    placeholder="(999) 999-9999"
                    v-mask="'(###) ###-####'"
                    v-model="user.phone"
                  >
                  </base-input>
                  <base-input
                    type="text"
                    label="Birthday"
                    placeholder="mm-dd-yyyy"
                    v-model="user.birthday"
                  >
                  </base-input>
                  <base-button native-type="submit" type="primary">
                    Save
                  </base-button>
                </div>
              </div>
            </form>
          </div>

          <!-- Preferences Tab -->
          <div v-if="activeTab === 'preferences'">
            <div v-if="companies.length <= 0" class="mt-3">
              <div class="font-icon-detail">
                <span style="font-size: 3em">
                  <i class="fas fa-exclamation-triangle"></i>
                </span>
                <h4 class="mt-3">No data available.</h4>
              </div>
            </div>
            <div v-else>
              <div class="row">
                <div class="col">
                  <b-table v-if="companies.length > 0" :items="companies"
                           :fields="['company', 'opt_out_of_emails', 'opt_out_of_sharing_photos_on_social_media']">
                    <template v-slot:cell(company)="row">
                      {{row.item.company_name}}
                    </template>
                    <template v-slot:cell(opt_out_of_emails)="row">
                      <base-checkbox v-model="row.item.opted_out_email"></base-checkbox>
                    </template>
                    <template v-slot:cell(opt_out_of_sharing_photos_on_social_media)="row">
                      <base-checkbox v-model="row.item.opted_out_photo_sharing"></base-checkbox>
                    </template>
                  </b-table>
                </div>
              </div>
              <base-button class="mt-4" v-on:click="saveMailingList()" type="primary">
                Save
              </base-button>
            </div>
          </div>

          <!-- Minors Tab -->
          <div v-if="activeTab === 'minors'">
            <div v-if="emptyMinors">
              <div class="font-icon-detail">
                <span style="font-size: 3em">
                  <i class="fas fa-child"></i>
                </span>
                <h4 class="mt-3">No minors registered.</h4>
              </div>
            </div>
            <div v-else>
              <p class="mb-4">I have signed for the following minors:</p>
              <div class="row">
                <div class="col">
                  <b-table striped hover :items="minors"
                           :fields="fields">
                    <template v-slot:cell(name)="row">
                      <span style="font-size: medium">{{row.item.first_name}} {{row.item.last_name}}</span>
                    </template>
                    <template v-slot:cell(actions)="row">
                      <base-button v-if="row.item.email == null" simple size="sm" v-on:click="convertMinorModal(row.item)" type="success">Create account</base-button>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div>

          <!-- Password Tab -->
          <div v-if="activeTab === 'security'">
            <base-alert v-if="error.length > 0" type="danger">
              <i class="fas fa-exclamation-triangle fa-2x"></i>
              {{error}}
            </base-alert>
            <base-alert v-if="message.length > 0" type="success">
              <i class="fas fa-check-circle fa-2x"></i>
              {{message}}
            </base-alert>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  type="password"
                  label="Old password"
                  v-model="old_password"
                >
                </base-input>
                <base-input
                  type="password"
                  label="New password"
                  v-model="new_password"
                >
                </base-input>
                <base-button v-on:click="changePassword()" type="primary">
                  Change password
                </base-button>
              </div>
            </div>
          </div>

          <!-- Payment Methods Tab -->
          <div v-if="activeTab === 'payment_methods'">
            <div class="row">
              <div class="col-12">
                <b-table striped hover :items="paymentMethods" :fields="paymentMethodFields">
                  <template v-slot:cell(card)="row">
                    <i :class="getCardIcon(row.item.brand)" class="mr-2"></i>
                    <span style="text-transform: capitalize">{{ row.item.brand }}</span>
                    •••• {{ row.item.last4 }}
                  </template>
                  <template v-slot:cell(expires)="row">
                    {{ row.item.exp_month }}/{{ row.item.exp_year }}
                  </template>
                  <template v-slot:cell(company)="row">
                    {{ row.item.company_name }}
                  </template>
                  <template v-slot:cell(actions)="row">
                    <el-dropdown>
                      <base-button
                        link
                        type="primary"
                        size="sm"
                      > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                      </base-button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <a class="dropdown-item" @click.prevent="deletePaymentMethod(row.item)" href="#">
                            <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Remove card
                          </a>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

        </card>
      </div>

      <div class="col-md-4">
        <card class="card-user">
          <p class="card-text"></p>
          <div class="author">
            <div class="block block-one"></div>
            <div class="block block-two"></div>
            <div class="block block-three"></div>
            <div class="block block-four"></div>
            <img class="avatar" :src="img" alt="..." />
            <p class="description">
              <span class="btn btn-info btn-sm btn-simple btn-file">Upload new image
                <input
                  accept="image/*"
                  @change="handlePreview"
                  type="file"
                  name="profile"
                  class="valid"
                  :multiple="false"
                  aria-invalid="false"
                />
              </span>
              <span v-show="newImage" class="btn btn-primary btn-sm btn-simple btn-file">Save
                <input
                  v-on:click="saveFile"
                />
              </span>
            </p>
          </div>
        </card>
      </div>
    </div>

    <modal :show.sync="modals.convert_minor"
           body-classes="p-0"
           modal-classes="modal-dialog-centered">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">
        <h4>Provide an email address for the new account</h4>
        <div class="row">
          <div class="col-md-12 col-lg-12 col-sm-12 col-12">
            <base-input v-model="selectedMinor.email" placeholder="Email"></base-input>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-lg-12">
            <base-button @click="modals.convert_minor = false" >Cancel</base-button>
            <base-button class="ml-2" @click="createMinorAccount()" type="primary">Create account</base-button>

          </div>
        </div>
      </card>
    </modal>
  </div>
</template>

<script>
  import { ImageUpload, BaseAlert, Modal } from 'src/components';
  import { Loading, Dropdown, DropdownMenu, DropdownItem } from 'element-ui';
  import { API_LOCATION } from '../../api-config'
  import swal from 'sweetalert2'
  const axios = require('axios')
  import { Auth } from 'aws-amplify'
  import {date, reverseDate} from '../../plugins/dateFormatter'
  import { BTable } from 'bootstrap-vue'

  export default {
      name: "MyProfile",
      components: {
        ImageUpload,
        Loading,
        BaseAlert,
        BTable,
        Modal,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem
      },
      data() {
        return {
          fields: ['name', 'actions'],
          modals: {
            convert_minor: false
          },
          user: {
            phone: '',
            first_name: '',
            last_name: '',
            birthday: '',
            email: '',
            img: ''
          },
          file: '',
          loading: false,
          old_password: '',
          new_password: '',
          error: '',
          message: '',
          img: '',
          newImage: false,
          companies: [],
          empty: false,
          emptyMinors: false,
          minors: [],
          selectedMinor: '',
          activeTab: 'profile',
          tabKey: 0,
          paymentMethods: [],
          paymentMethodFields: ['card', 'expires', 'company', 'actions']
        }
      },
      computed: {
        tabs() {
          let allTabs = [
            { name: 'Profile', key: 'profile', icon: 'fas fa-user' },
            { name: 'Communication preferences', key: 'preferences', icon: 'fas fa-envelope' },
            { name: 'Minors', key: 'minors', icon: 'fas fa-child', showIf: !this.emptyMinors },
            { name: 'Password', key: 'security', icon: 'fas fa-lock' },
            { name: 'Payment methods', key: 'payment_methods', icon: 'fas fa-credit-card', showIf: this.paymentMethods.length > 0 }
          ]
          return allTabs
            .filter(tab => tab.showIf === undefined || tab.showIf)
            .map(tab => ({ ...tab, active: tab.key === this.activeTab }))
        }
      },
      methods: {
        switchTab(index) {
          this.activeTab = this.tabs[index].key
          this.tabKey++
        },
        getCardIcon(brand) {
          const icons = {
            visa: 'fab fa-cc-visa',
            mastercard: 'fab fa-cc-mastercard',
            amex: 'fab fa-cc-amex',
            discover: 'fab fa-cc-discover',
            diners: 'fab fa-cc-diners-club',
            jcb: 'fab fa-cc-jcb'
          }
          return icons[brand && brand.toLowerCase()] || 'fas fa-credit-card'
        },
        async deletePaymentMethod(item) {
          let result = await swal.fire({
            title: 'Are you sure?',
            text: `Remove ${item.brand} •••• ${item.last4}?`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove it',
            reverseButtons: true
          })

          if (result.value) {
            this.loading = true
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let url = API_LOCATION + 'customer/payment-methods/' + item.id + '?gateway=' + item.payment_gateway

            this.axios.delete(url, config)
              .then(async response => {
                this.loading = false
                await this.getPaymentMethods()
                if (this.paymentMethods.length === 0) {
                  this.activeTab = 'profile'
                }
                swal.fire('Removed', 'Payment method has been removed.', 'success')
              })
              .catch(err => {
                this.loading = false
                console.log(err)
                swal.fire('Error', 'Could not remove payment method.', 'error')
              })
          }
        },
        convertMinorModal(item){
          this.modals.convert_minor = true
          this.selectedMinor = item
        },
        async createMinorAccount(){
          this.loading = true
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'minors'

          let data = {
            minor: this.selectedMinor
          }

          this.modals.convert_minor = false
          this.axios.post(url, data, config)
            .then(response => {
              this.loading = false
              this.getMinors()
            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        },
        getAuthToken() {
          return new Promise ( function(resolve) {
            Auth.currentSession()
              .then(data =>{
                resolve(data.idToken.jwtToken)
              })
              .catch(err => console.log(err))
          })
        },
        getDate(d){
         return date(d)
        },
        reverseDate(d){
          return reverseDate(d)
        },
        async changePassword() {
          this.loading = true
          let user = await Auth.currentAuthenticatedUser()
          try {
            await Auth.changePassword(user, this.old_password, this.new_password)
            this.message = 'Password successfully changed!'
            this.error = ''
            this.new_password = ''
            this.old_password = ''
            this.loading = false
          } catch (err) {

            if(err.code === 'InvalidParameterException' || err.code === 'InvalidPasswordException'){
              this.error = 'Password must be at least 6 characters long and must have uppercase characters.'
              this.message = ''
            } else if (err.code === 'NotAuthorizedException') {
              this.error = 'Old password is not correct.'
              this.message = ''
            } else if (err.code === 'LimitExceededException') {
              this.error = 'Too many requests. Please try again later.'
              this.message = ''
            }
            this.new_password = ''
            this.old_password = ''
            this.loading = false
          }
        },
        async updateProfile() {
          this.loading = true
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'profile'
          let data = this.user
          data.birthday = this.reverseDate(data.birthday)


          axios.put(url, data, config)
            .then(response => {
              this.loading = false
              //refresh the page
              axios.get(url, config)
                .then(response => {
                  let user = response.data[0]
                  if(user.birthday.includes('0000')){
                    user.birthday = ''
                  } else {
                    user.birthday = this.getDate(user.birthday)
                  }

                  this.user = user
                  this.img = user.img
                  localStorage.setItem('user', JSON.stringify(user))
                })

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        },
        handlePreview(event) {
          let file = event.target.files[0];
          console.log(file)
          this.img = URL.createObjectURL(file);
          console.log(this.img)
          this.file = file
          this.newImage = true
          this.$emit('change', file);
        },
        async saveFile() {
          this.loading = true
          if(this.file != ''){
            this.uploadUrl = await this.getSignedURL(this.file)
            let uploaded = await this.uploadFile(this.file)
            //Get the image URL
            let imgUrl = this.uploadUrl.split("?")
            this.user.img = imgUrl[0]
            localStorage.setItem('user', JSON.stringify(this.user))
            //update the db
            let token = await this.getAuthToken()
            let config = {headers: {Authorization: token}}
            let url = API_LOCATION + 'profile/picture'
            let data = {img: this.user.img}
            axios.put(url, data, config)
              .then(response => {
                this.loading = false
              })
              .catch(err => {
                this.loading = false
                console.log(err)
                if(err.response.status == 401){
                  this.$router.push('/forbidden')
                }
              })
          }
        },
        uploadFile (file) {
          let url = this.uploadUrl
          let config = { headers: {
              'Content-Type': file.type
            }}

          let payload = file
          return new Promise ( function(resolve) {
            axios.put(url, payload, config)
              .then((res) => {
                resolve(res)
              })
              .catch((err) => {
                console.error(err)
              })
          })
        },
        async getSignedURL (file) {
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'upload'

          let payload = {
            filePath: file.name,
            contentType: file.type
          }

          return new Promise ( function(resolve) {
            axios.post(url, payload, config)
              .then((res) => {
                resolve(res.data)
              })
              .catch((err) => {
                console.error(err)
              })
          })
        },
        async saveMailingList(){
          this.loading = true
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/subscribe'

          let data = {
            companies: this.companies
          }

          this.axios.post(url, data, config)
            .then(response => {
              this.loading = false
              this.getCompanies()

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        },
        async getCompanies() {
          this.loading = true
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies'

          this.axios.get(url, config)
            .then(response => {
              this.loading = false
              this.companies = response.data

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })

        },
        async getMinors() {
          this.loading = true
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'minors'

          this.axios.get(url, config)
            .then(response => {
              this.loading = false
              this.minors = response.data
              if(this.minors.length <= 0){
                this.emptyMinors = true
              }

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })

        },
        async getPaymentMethods() {
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'customer/payment-methods'

          return this.axios.get(url, config)
            .then(response => {
              this.paymentMethods = response.data
            })
            .catch(err => {
              console.log(err)
              if(err.response && err.response.status == 401){
                this.$router.push('/forbidden')
              }
            })
        }
      },
      mounted(){
        this.user = JSON.parse(localStorage.getItem('user'))
        if(this.user.birthday.includes('0000')){
          this.user.birthday = ''
        }
        this.img = this.user.img
        this.getCompanies()
        this.getMinors()
        this.getPaymentMethods()
      }

    }
</script>

<style scoped>
  #loading {
    min-height: 1000px;
    display: flex;
    align-items: center;
  }

  .el-loading-spinner .path {
    stroke: #6aff8d !important;
  }

  .el-loading-mask {
    background: rgba(0, 0, 0, 0.7) !important;
  }

</style>
